import type {
  AgentWorkbenchCollectionStatus,
  AgentWorkbenchSessionItem,
} from "@inf-monkeys-tech/monkeys";
import {
  AlertCircle,
  Bot,
  CheckCircle2,
  Ellipsis,
  Image as ImageIcon,
  Loader2,
  MessageSquare,
  Pencil,
  Pin,
  RefreshCw,
  Search,
  SquarePen,
  Trash2,
  X,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { useMonkeysLocale, useMonkeysLocaleMessages } from "../../provider";
import { cn } from "../layout";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from "../primitives";
import type { AgentWorkbenchVariant } from "./appearance";

export interface AgentWorkbenchSidebarSearchProps {
  value: string;
  label: string;
  placeholder?: string;
  className?: string;
  variant?: AgentWorkbenchVariant;
  onChange: (value: string) => void;
}

export function AgentWorkbenchSidebarSearch({
  value,
  label,
  placeholder = label,
  className,
  variant = "default",
  onChange,
}: AgentWorkbenchSidebarSearchProps) {
  const messages = useMonkeysLocaleMessages().agentWorkbench.navigation;
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleShortcut = (event: KeyboardEvent) => {
      if (
        !(event.ctrlKey || event.metaKey) ||
        event.key.toLocaleLowerCase() !== "k"
      )
        return;
      event.preventDefault();
      searchRef.current?.focus();
      searchRef.current?.select();
    };
    window.addEventListener("keydown", handleShortcut);
    return () => window.removeEventListener("keydown", handleShortcut);
  }, []);

  return (
    <InputGroup
      className={cn(
        variant === "trend"
          ? "h-12 rounded-[var(--radius)] border-border bg-card text-foreground"
          : "h-10 rounded-lg bg-card",
        className,
      )}
    >
      <InputGroupAddon>
        <Search className="size-4" aria-hidden="true" />
      </InputGroupAddon>
      <InputGroupInput
        ref={searchRef}
        type="search"
        value={value}
        aria-label={label}
        placeholder={placeholder}
        className="min-w-0 [&::-webkit-search-cancel-button]:hidden"
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key !== "Escape") return;
          onChange("");
          event.currentTarget.blur();
        }}
      />
      {value ? (
        <InputGroupAddon align="inline-end">
          <InputGroupButton
            size="icon-xs"
            aria-label={messages.clearSearch}
            onClick={() => onChange("")}
          >
            <X className="size-3.5" aria-hidden="true" />
          </InputGroupButton>
        </InputGroupAddon>
      ) : (
        <InputGroupAddon align="inline-end">
          <kbd className="border border-border px-1.5 py-0.5 text-[10px]">
            Ctrl K
          </kbd>
        </InputGroupAddon>
      )}
    </InputGroup>
  );
}

export function AgentWorkbenchSidebarNewSessionButton({
  label,
  className,
  variant = "default",
  onClick,
}: {
  label: string;
  className?: string;
  variant?: AgentWorkbenchVariant;
  onClick: () => void;
}) {
  return (
    <Button
      type="button"
      variant={variant === "trend" ? "default" : "ghost"}
      className={cn(
        variant === "trend"
          ? "h-11 w-full justify-center gap-2 rounded-[var(--radius)] border border-primary/60 bg-primary px-3 font-semibold text-primary-foreground hover:bg-primary/90"
          : "h-9 w-full justify-start gap-2 px-3 text-sm",
        className,
      )}
      onClick={onClick}
    >
      <SquarePen className="size-4" aria-hidden="true" />
      {label}
    </Button>
  );
}

function SessionAvatar({ item }: { item: AgentWorkbenchSessionItem }) {
  const [failed, setFailed] = useState(false);
  if (item.thumbnailUrl && !failed) {
    return (
      <img
        src={item.thumbnailUrl}
        alt=""
        className="size-full object-cover"
        loading="lazy"
        onError={() => setFailed(true)}
      />
    );
  }
  return <ImageIcon className="size-5" aria-hidden="true" />;
}

function SessionMeta({ item }: { item: AgentWorkbenchSessionItem }) {
  const { locale } = useMonkeysLocale();
  const messages = useMonkeysLocaleMessages().agentWorkbench.navigation;
  return (
    <span className="flex min-w-0 items-center gap-1.5">
      <span className="min-w-0 truncate text-xs text-foreground/80">
        {new Intl.DateTimeFormat(locale, {
          dateStyle: "medium",
          timeStyle: "short",
        }).format(new Date(item.updatedAt))}
      </span>
      {item.mode ? (
        <span
          className={cn(
            "shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-medium leading-none",
            item.mode === "work"
              ? "bg-primary/10 text-primary"
              : "bg-muted text-foreground",
          )}
        >
          {item.mode === "work" ? messages.work : messages.chat}
        </span>
      ) : null}
    </span>
  );
}

function StatusIndicator({
  status,
}: {
  status: AgentWorkbenchSessionItem["status"];
}) {
  const messages = useMonkeysLocaleMessages().agentWorkbench.navigation;
  if (status === "running") {
    return (
      <Loader2
        className="size-4 animate-spin text-primary"
        aria-label={messages.running}
      />
    );
  }
  if (status === "completed") {
    return (
      <CheckCircle2
        className="size-4 text-primary"
        aria-label={messages.completed}
      />
    );
  }
  if (status === "error") {
    return (
      <AlertCircle
        className="size-4 text-destructive-text"
        aria-label={messages.failed}
      />
    );
  }
  return null;
}

export interface AgentWorkbenchSessionListProps {
  sessions: AgentWorkbenchSessionItem[];
  selectedSessionId?: string;
  status?: AgentWorkbenchCollectionStatus;
  error?: string;
  query?: string;
  emptyLabel: string;
  noResultsLabel: string;
  retryLabel?: string;
  groupByMode?: boolean;
  showHeading?: boolean;
  variant?: "default" | "trend";
  className?: string;
  itemClassName?: string;
  capabilities?: {
    pin?: boolean;
    rename?: boolean;
    delete?: boolean;
  };
  onSelect: (sessionId: string) => void;
  onTogglePin?: (sessionId: string, pinned: boolean) => void;
  onRename?: (sessionId: string, title: string) => void;
  onDelete?: (sessionId: string) => void;
  onRetry?: () => void;
}

export function AgentWorkbenchSessionList({
  sessions,
  selectedSessionId,
  status = "ready",
  error,
  query = "",
  emptyLabel,
  noResultsLabel,
  retryLabel,
  groupByMode = false,
  showHeading = false,
  variant = "default",
  className,
  itemClassName,
  capabilities,
  onSelect,
  onTogglePin,
  onRename,
  onDelete,
  onRetry,
}: AgentWorkbenchSessionListProps) {
  const messages = useMonkeysLocaleMessages().agentWorkbench.navigation;
  const renameInputRef = useRef<HTMLInputElement>(null);
  const [editingSessionId, setEditingSessionId] = useState<string>();
  const [editingTitle, setEditingTitle] = useState("");

  useEffect(() => {
    if (!editingSessionId) return;
    renameInputRef.current?.focus();
    renameInputRef.current?.select();
  }, [editingSessionId]);

  const cancelRename = () => {
    setEditingSessionId(undefined);
    setEditingTitle("");
  };
  const saveRename = (item: AgentWorkbenchSessionItem) => {
    const title = editingTitle.trim();
    if (title && title !== item.title) onRename?.(item.id, title);
    cancelRename();
  };

  if (status === "loading" && sessions.length === 0) {
    return (
      <div
        className="space-y-2 px-1 py-3"
        role="status"
        aria-label={messages.loading}
      >
        {Array.from({ length: 4 }, (_, index) => (
          <div
            key={index}
            aria-hidden="true"
            className={cn(
              "animate-pulse rounded-[var(--radius)] bg-muted/70",
              variant === "trend" ? "h-10" : "h-14",
            )}
          />
        ))}
      </div>
    );
  }
  if (status === "error") {
    return (
      <div className="flex flex-col items-center gap-3 px-3 py-8 text-center">
        <p
          className="max-w-full break-words text-sm text-muted-foreground"
          role="alert"
        >
          {error}
        </p>
        {onRetry ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="gap-1.5 px-0 text-sm text-primary hover:text-primary"
            onClick={onRetry}
          >
            <RefreshCw className="size-3.5" aria-hidden="true" />
            {retryLabel ?? messages.retry}
          </Button>
        ) : null}
      </div>
    );
  }
  if (sessions.length === 0) {
    return (
      <div className="px-3 py-8 text-center text-sm text-muted-foreground">
        {query ? noResultsLabel : emptyLabel}
      </div>
    );
  }

  const renderTrendItems = (items: AgentWorkbenchSessionItem[]) => (
    <div className="space-y-3">
      {items.map((item) => (
        <div
          key={item.id}
          aria-current={item.id === selectedSessionId ? "page" : undefined}
          data-agent-session-status={item.status ?? "idle"}
          aria-busy={item.status === "running" ? "true" : undefined}
          className="group flex h-10 w-full min-w-0 items-center rounded-[var(--radius)] border border-transparent bg-muted/60 text-foreground transition hover:bg-muted focus-within:border-ring focus-within:ring-2 focus-within:ring-ring aria-[current=page]:border-primary aria-[current=page]:bg-primary/10"
        >
          <button
            type="button"
            title={item.title}
            className="h-full min-w-0 flex-1 truncate px-3 text-left text-sm font-semibold"
            onClick={() => onSelect(item.id)}
          >
            {item.title}
          </button>
          {capabilities?.delete ? (
            <span
              data-agent-workbench-item-actions
              className="mr-1 shrink-0 opacity-0 transition-opacity group-aria-[current=page]:opacity-100 group-focus-within:opacity-100 group-hover:opacity-100"
              onClick={(event) => event.stopPropagation()}
              onKeyDown={(event) => event.stopPropagation()}
            >
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label={messages.actions}
                    className="size-8 text-muted-foreground"
                  >
                    <Ellipsis className="size-4" aria-hidden="true" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    className="text-destructive-text"
                    onSelect={() => onDelete?.(item.id)}
                  >
                    <Trash2 className="mr-2 size-4" aria-hidden="true" />
                    {messages.delete}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </span>
          ) : null}
        </div>
      ))}
    </div>
  );

  const renderItems = (items: AgentWorkbenchSessionItem[]) => (
    <div className="space-y-1">
      {items.map((item) => {
        const percent =
          item.contextUsage && item.contextUsage.maxTokens > 0
            ? Math.min(
                100,
                Math.round(
                  (item.contextUsage.usedTokens / item.contextUsage.maxTokens) *
                    100,
                ),
              )
            : undefined;
        return (
          <div
            key={item.id}
            aria-current={item.id === selectedSessionId ? "page" : undefined}
            className={cn(
              "group flex w-full min-w-0 items-center gap-3 rounded-lg border border-transparent px-3 py-2 text-left hover:bg-muted aria-[current=page]:border-primary aria-[current=page]:bg-muted",
              itemClassName,
            )}
          >
            {editingSessionId === item.id ? (
              <div className="flex min-w-0 flex-1 items-center gap-3">
                <span className="flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-muted">
                  <SessionAvatar item={item} />
                </span>
                <span className="min-w-0 flex-1">
                  <Input
                    ref={renameInputRef}
                    value={editingTitle}
                    aria-label={messages.rename}
                    appearance="compact"
                    className="h-6 w-full border-primary px-1.5 text-sm font-medium"
                    onChange={(event) => setEditingTitle(event.target.value)}
                    onClick={(event) => event.stopPropagation()}
                    onBlur={() => saveRename(item)}
                    onKeyDown={(event) => {
                      event.stopPropagation();
                      if (event.key === "Enter") {
                        event.preventDefault();
                        saveRename(item);
                      } else if (event.key === "Escape") {
                        event.preventDefault();
                        cancelRename();
                      }
                    }}
                  />
                  <SessionMeta item={item} />
                </span>
              </div>
            ) : (
              <button
                type="button"
                title={item.title}
                className="flex min-w-0 flex-1 items-center gap-3 text-left"
                onClick={() => onSelect(item.id)}
              >
                <span className="flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-muted">
                  <SessionAvatar item={item} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium">
                    {item.title}
                  </span>
                  <SessionMeta item={item} />
                </span>
              </button>
            )}
            <span
              data-agent-workbench-item-actions
              className="flex shrink-0 items-center gap-1"
            >
              {percent !== undefined && item.status === "idle" ? (
                <span
                  className="text-xs text-foreground"
                  aria-label={messages.contextUsage({ percent })}
                >
                  {percent}%
                </span>
              ) : (
                <StatusIndicator status={item.status} />
              )}
              {capabilities?.pin ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label={item.pinned ? messages.unpin : messages.pin}
                  className={cn(
                    "size-8 text-muted-foreground opacity-0 group-hover:opacity-100",
                    item.pinned && "text-primary opacity-100",
                  )}
                  onClick={(event) => {
                    event.stopPropagation();
                    onTogglePin?.(item.id, !item.pinned);
                  }}
                >
                  <Pin
                    className={cn("size-4", item.pinned && "fill-current")}
                  />
                </Button>
              ) : null}
              {capabilities?.rename || capabilities?.delete ? (
                <span
                  onClick={(event) => event.stopPropagation()}
                  onKeyDown={(event) => event.stopPropagation()}
                >
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="size-8"
                        aria-label={messages.actions}
                      >
                        <Ellipsis className="size-4" aria-hidden="true" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {capabilities.rename ? (
                        <DropdownMenuItem
                          onSelect={() => {
                            setEditingSessionId(item.id);
                            setEditingTitle(item.title);
                          }}
                        >
                          <Pencil className="mr-2 size-4" aria-hidden="true" />
                          {messages.rename}
                        </DropdownMenuItem>
                      ) : null}
                      {capabilities.delete ? (
                        <DropdownMenuItem
                          className="text-destructive-text"
                          onSelect={() => onDelete?.(item.id)}
                        >
                          <Trash2 className="mr-2 size-4" aria-hidden="true" />
                          {messages.delete}
                        </DropdownMenuItem>
                      ) : null}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </span>
              ) : null}
            </span>
          </div>
        );
      })}
    </div>
  );

  return (
    <div className={cn("space-y-2", className)}>
      {showHeading && !groupByMode ? (
        <h3 className="mb-2 px-3 text-xs font-medium text-muted-foreground">
          {messages.recentSessions}
        </h3>
      ) : null}
      {variant === "trend"
        ? renderTrendItems(sessions)
        : groupByMode
          ? [
              {
                id: "chatbot",
                label: messages.chats,
                icon: MessageSquare,
                items: sessions.filter((item) => item.mode !== "work"),
              },
              {
                id: "agent",
                label: messages.agents,
                icon: Bot,
                items: sessions.filter((item) => item.mode === "work"),
              },
            ].map(({ id, label, icon: Icon, items }) => (
              <details key={id} open aria-label={label}>
                <summary className="flex h-9 cursor-pointer list-none items-center gap-2 rounded-md px-3 text-sm font-semibold hover:bg-muted">
                  <Icon
                    className="size-4 shrink-0 text-muted-foreground"
                    aria-hidden="true"
                  />
                  <span className="min-w-0 truncate">{label}</span>
                </summary>
                {renderItems(items)}
              </details>
            ))
          : renderItems(sessions)}
    </div>
  );
}
